/**
 * Analytics Service - Hybrid tracking with GA4 and custom database
 * 
 * This service sends:
 * - Sanitized behavioral data to GA4 (for general analytics)
 * - Detailed financial metrics to our database (for business intelligence)
 */

import { logger } from '@/utils/logger'

export interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
  customData?: Record<string, any>;
}

class AnalyticsService {
  private gaEnabled: boolean;
  private customEnabled: boolean;

  constructor() {
    this.gaEnabled = import.meta.env.VITE_GA_ENABLED === 'true';
    this.customEnabled = true; // Always track to our DB
  }

  /**
   * Track page view
   */
  trackPageView(path: string, title?: string) {
    // GA4
    if (this.gaEnabled && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: this.sanitizePath(path),
        page_title: title,
      });
    }

    // Custom DB (detailed)
    if (this.customEnabled) {
      this.sendToCustomAPI('page_view', {
        path,
        title,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Track generic event
   */
  trackEvent(event: AnalyticsEvent) {
    const { category, action, label, value, customData } = event;

    // GA4 (sanitized)
    if (this.gaEnabled && window.gtag) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }

    // Custom DB (full details)
    if (this.customEnabled) {
      this.sendToCustomAPI('event', {
        category,
        action,
        label,
        value,
        ...customData,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Track feature usage (CUSTOM DB ONLY - too detailed for GA4)
   */
  trackFeatureUsage(feature: string, details: Record<string, any>) {
    // Only send to custom DB - too detailed for GA4
    if (this.customEnabled) {
      this.sendToCustomAPI('feature_usage', {
        feature,
        details,
        timestamp: new Date().toISOString(),
      });
    }

    // Send simplified version to GA4
    if (this.gaEnabled && window.gtag) {
      window.gtag('event', 'feature_used', {
        event_category: 'engagement',
        event_label: feature,
      });
    }
  }

  /**
   * Track financial metrics (CUSTOM DB ONLY)
   */
  trackFinancialMetric(metric: {
    type: 'portfolio_value' | 'return' | 'risk_score' | 'allocation';
    value?: number;
    metadata?: Record<string, any>;
  }) {
    // NEVER send to GA4 - PII/financial data
    if (this.customEnabled) {
      this.sendToCustomAPI('financial_metric', {
        ...metric,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Track conversion/goal (BOTH)
   */
  trackConversion(conversionType: string, value?: number) {
    // GA4 (for funnel analysis)
    if (this.gaEnabled && window.gtag) {
      window.gtag('event', 'conversion', {
        event_category: 'conversion',
        event_label: conversionType,
        value: value,
      });
    }

    // Custom DB (detailed)
    if (this.customEnabled) {
      this.sendToCustomAPI('conversion', {
        type: conversionType,
        value,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Track A/B test assignment (CUSTOM DB ONLY)
   */
  trackABTest(testName: string, variant: string) {
    // Custom DB only - we need this data for analysis
    if (this.customEnabled) {
      this.sendToCustomAPI('ab_test', {
        test_name: testName,
        variant,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Track error (BOTH)
   */
  trackError(error: Error, context?: Record<string, any>) {
    // GA4 - sanitized error
    if (this.gaEnabled && window.gtag) {
      window.gtag('event', 'exception', {
        description: error.message,
        fatal: false,
      });
    }

    // Custom DB - detailed error
    if (this.customEnabled) {
      this.sendToCustomAPI('error', {
        message: error.message,
        stack: error.stack,
        context,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Sanitize path - remove any PII
   */
  private sanitizePath(path: string): string {
    // Remove query parameters that might contain PII
    return path.split('?')[0];
  }

  /**
   * Send to custom analytics API
   */
  private async sendToCustomAPI(eventType: string, data: any) {
    try {
      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const response = await fetch(`${baseURL}/api/analytics/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_type: eventType,
          data,
        }),
        // Don't block user actions waiting for analytics
        keepalive: true,
      });

      // Log success for debugging (only in development)
      if (import.meta.env.DEV && response.ok) {
        logger.debug(`Event tracked: ${eventType}`, data);
      }
    } catch (error) {
      // Fail silently - don't disrupt user experience
      // Only log in development mode
      if (import.meta.env.DEV) {
        console.debug('Analytics API error (ignored):', error);
      }
    }
  }

  /**
   * Set user ID (hashed for GA4)
   */
  setUserId(userId: string) {
    // GA4 - use hashed ID
    if (this.gaEnabled && window.gtag) {
      const hashedId = `user_${this.hashUserId(userId)}`;
      window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
        user_id: hashedId,
      });
    }

    // Custom DB - use real ID
    if (this.customEnabled) {
      this.sendToCustomAPI('identify', {
        user_id: userId,
      });
    }
  }

  /**
   * Clear user ID (on logout)
   */
  clearUserId() {
    if (this.gaEnabled && window.gtag) {
      window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
        user_id: null,
      });
    }

    if (this.customEnabled) {
      this.sendToCustomAPI('logout', {
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Simple hash function for user IDs
   */
  private hashUserId(userId: string): string {
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }
}

export const analytics = new AnalyticsService();

// Type declaration for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}
